import jsPDF from 'jspdf';

const PRIMARY = [30, 64, 175];
const DARK = [15, 23, 42];
const GRAY = [100, 116, 139];
const GREEN = [16, 185, 129];
const AMBER = [245, 158, 11];
const RED = [239, 68, 68];
const BLUE = [59, 130, 246];
const WHITE = [255, 255, 255];

function addFooter(doc, pageNum, totalPages, guideTitle) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...GRAY);
  doc.setLineWidth(0.3);
  doc.line(20, h - 15, w - 20, h - 15);
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text(`PunchTM - ${guideTitle}`, 20, h - 10);
  doc.text(`Page ${pageNum} / ${totalPages}`, w - 20, h - 10, { align: 'right' });
}

function checkPageBreak(doc, y, needed = 30) {
  const h = doc.internal.pageSize.getHeight();
  if (y + needed > h - 25) {
    doc.addPage();
    return 25;
  }
  return y;
}

function wrapText(doc, text, x, y, maxWidth, lineHeight = 5.5) {
  const lines = doc.splitTextToSize(text, maxWidth);
  lines.forEach(line => {
    y = checkPageBreak(doc, y, lineHeight + 2);
    doc.text(line, x, y);
    y += lineHeight;
  });
  return y;
}

export function generateGuidePDF(guide) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const w = doc.internal.pageSize.getWidth();
  const contentWidth = w - 40;

  // ===== TITLE PAGE =====
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, w, 100, 'F');
  
  doc.setTextColor(...WHITE);
  doc.setFontSize(32);
  doc.text(guide.title, w / 2, 50, { align: 'center' });
  
  doc.setFontSize(14);
  doc.text(guide.subtitle || '', w / 2, 65, { align: 'center', maxWidth: contentWidth });
  
  doc.setTextColor(...DARK);
  doc.setFontSize(12);
  doc.text('PunchTM - Systeme de pointage industriel', w / 2, 120, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setTextColor(...GRAY);
  const today = new Date().toLocaleDateString('fr-CA');
  doc.text(`Document genere le ${today}`, w / 2, 135, { align: 'center' });

  // ===== TABLE OF CONTENTS =====
  doc.addPage();
  let y = 25;
  doc.setFontSize(22);
  doc.setTextColor(...DARK);
  doc.text('Table des matieres', 20, y);
  y += 15;

  guide.sections.forEach((section, sIdx) => {
    y = checkPageBreak(doc, y, 12);
    doc.setFontSize(12);
    doc.setTextColor(...PRIMARY);
    doc.text(`${sIdx + 1}. ${section.title}`, 20, y);
    y += 7;

    section.content?.forEach((sub, subIdx) => {
      y = checkPageBreak(doc, y, 7);
      doc.setFontSize(10);
      doc.setTextColor(...GRAY);
      doc.text(`${sIdx + 1}.${subIdx + 1}  ${sub.title}`, 30, y);
      y += 6;
    });
    y += 3;
  });

  // ===== CONTENT =====
  guide.sections.forEach((section, sIdx) => {
    doc.addPage();
    y = 25;

    // Section header with colored bar
    doc.setFillColor(...PRIMARY);
    doc.rect(20, y - 5, 4, 12, 'F');
    doc.setFontSize(20);
    doc.setTextColor(...DARK);
    doc.text(`${sIdx + 1}. ${section.title}`, 28, y + 4);
    y += 15;

    if (section.overview) {
      doc.setFontSize(10);
      doc.setTextColor(...GRAY);
      y = wrapText(doc, section.overview, 20, y, contentWidth);
      y += 5;
    }

    section.content?.forEach((sub, subIdx) => {
      y = checkPageBreak(doc, y, 40);

      // Subsection title
      doc.setFillColor(240, 245, 255);
      doc.rect(20, y - 4, contentWidth, 10, 'F');
      doc.setFontSize(14);
      doc.setTextColor(...PRIMARY);
      doc.text(`${sIdx + 1}.${subIdx + 1}  ${sub.title}`, 24, y + 3);
      y += 14;

      // Description
      if (sub.description) {
        doc.setFontSize(10);
        doc.setTextColor(...DARK);
        y = wrapText(doc, sub.description, 20, y, contentWidth);
        y += 4;
      }

      // When to use
      if (sub.whenToUse) {
        y = checkPageBreak(doc, y, 20);
        doc.setFillColor(239, 246, 255);
        const lines = doc.splitTextToSize(sub.whenToUse, contentWidth - 14);
        const boxH = lines.length * 5 + 6;
        doc.roundedRect(20, y - 2, contentWidth, boxH, 2, 2, 'F');
        doc.setFontSize(8);
        doc.setTextColor(...BLUE);
        doc.text('QUAND UTILISER', 27, y + 2);
        doc.setFontSize(9);
        doc.setTextColor(30, 64, 175);
        lines.forEach((line, i) => {
          doc.text(line, 27, y + 8 + i * 5);
        });
        y += boxH + 4;
      }

      // Steps
      if (sub.steps?.length) {
        y = checkPageBreak(doc, y, 15);
        doc.setFontSize(11);
        doc.setTextColor(...DARK);
        doc.text('Etapes', 20, y);
        y += 6;

        sub.steps.forEach((step, i) => {
          y = checkPageBreak(doc, y, 12);
          // Step number circle
          doc.setFillColor(...PRIMARY);
          doc.circle(25, y - 1.5, 3, 'F');
          doc.setFontSize(8);
          doc.setTextColor(...WHITE);
          doc.text(`${i + 1}`, 25, y - 0.5, { align: 'center' });
          // Step text
          doc.setFontSize(9.5);
          doc.setTextColor(...DARK);
          const stepLines = doc.splitTextToSize(step, contentWidth - 20);
          stepLines.forEach((line, li) => {
            doc.text(line, 32, y - 1 + li * 5);
          });
          y += Math.max(stepLines.length * 5, 6) + 2;
        });
        y += 3;
      }

      // Result
      if (sub.result) {
        y = checkPageBreak(doc, y, 18);
        doc.setFillColor(236, 253, 245);
        const lines = doc.splitTextToSize(sub.result, contentWidth - 14);
        const boxH = lines.length * 5 + 6;
        doc.roundedRect(20, y - 2, contentWidth, boxH, 2, 2, 'F');
        doc.setFontSize(8);
        doc.setTextColor(...GREEN);
        doc.text('RESULTAT ATTENDU', 27, y + 2);
        doc.setFontSize(9);
        doc.setTextColor(5, 150, 105);
        lines.forEach((line, i) => {
          doc.text(line, 27, y + 8 + i * 5);
        });
        y += boxH + 4;
      }

      // Tips
      sub.tips?.forEach(tip => {
        y = checkPageBreak(doc, y, 18);
        doc.setFillColor(239, 246, 255);
        const lines = doc.splitTextToSize(tip, contentWidth - 14);
        const boxH = lines.length * 5 + 6;
        doc.roundedRect(20, y - 2, contentWidth, boxH, 2, 2, 'F');
        doc.setFontSize(8);
        doc.setTextColor(...BLUE);
        doc.text('CONSEIL', 27, y + 2);
        doc.setFontSize(9);
        doc.setTextColor(30, 64, 175);
        lines.forEach((line, i) => {
          doc.text(line, 27, y + 8 + i * 5);
        });
        y += boxH + 4;
      });

      // Warnings
      sub.warnings?.forEach(w => {
        y = checkPageBreak(doc, y, 18);
        doc.setFillColor(255, 251, 235);
        const lines = doc.splitTextToSize(w, contentWidth - 14);
        const boxH = lines.length * 5 + 6;
        doc.roundedRect(20, y - 2, contentWidth, boxH, 2, 2, 'F');
        doc.setFontSize(8);
        doc.setTextColor(...AMBER);
        doc.text('ATTENTION', 27, y + 2);
        doc.setFontSize(9);
        doc.setTextColor(180, 83, 9);
        lines.forEach((line, i) => {
          doc.text(line, 27, y + 8 + i * 5);
        });
        y += boxH + 4;
      });

      // Errors
      if (sub.errors?.length) {
        y = checkPageBreak(doc, y, 20);
        doc.setFontSize(10);
        doc.setTextColor(...RED);
        doc.text('Erreurs frequentes', 20, y);
        y += 6;

        sub.errors.forEach(err => {
          y = checkPageBreak(doc, y, 18);
          doc.setFillColor(254, 242, 242);
          doc.roundedRect(20, y - 2, contentWidth, 16, 2, 2, 'F');
          doc.setFontSize(8);
          doc.setTextColor(...RED);
          doc.text('Probleme:', 24, y + 2);
          doc.setFontSize(9);
          doc.setTextColor(...DARK);
          doc.text(doc.splitTextToSize(err.problem, contentWidth / 2 - 10).join(' '), 48, y + 2);
          doc.setFontSize(8);
          doc.setTextColor(...GREEN);
          doc.text('Solution:', 24, y + 9);
          doc.setFontSize(9);
          doc.setTextColor(...DARK);
          doc.text(doc.splitTextToSize(err.solution, contentWidth / 2 - 10).join(' '), 48, y + 9);
          y += 20;
        });
      }

      y += 8;
    });
  });

  // Add footers
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(doc, i, totalPages, guide.title);
  }

  const filename = `PunchTM_${guide.id.replace(/-/g, '_')}_${today}.pdf`;
  doc.save(filename);
}
